/**
 * useJiraData — Hook that fetches real Jira data for the active project.
 * Provides loading states and auto-refresh when project changes.
 * Components use this instead of reading from mockData.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import {
  fetchProjects,
  fetchSprintSummary,
  fetchBacklog,
  fetchRecentActivity,
  searchIssues,
  calculateBurndown,
  fetchBoards,
  fetchSprints,
  JiraProject,
  JiraIssue,
  JiraSprint,
  JiraBoard,
  SprintSummary,
  BurndownPoint,
} from '../services/jiraData'
export interface JiraDataState {
  projects: JiraProject[]
  projectsLoading: boolean
  projectsError: string | null
  sprintSummary: SprintSummary | null
  sprintLoading: boolean
  sprintError: string | null
  allSprints: JiraSprint[]
  allSprintsLoading: boolean
  boards: import('../services/jiraData').JiraBoard[]
  boardsLoading: boolean
  backlog: JiraIssue[]
  backlogLoading: boolean
  recentActivity: JiraIssue[]
  activityLoading: boolean
  burndownData: BurndownPoint[]
  kpis: {
    totalIssues: number
    openIssues: number
    inProgress: number
    done: number
    velocity: number
    blockers: number
  } | null
  refreshProjects: () => Promise<void>
  refreshProjectData: (projectKey: string) => Promise<void>
  refreshAll: (projectKey: string) => Promise<void>
}

/**
 * @param activeProjectKey - Project key to fetch data for (undefined = don't fetch)
 * @param isGlobalView - Whether in global view
 * @param enabled - Whether the hook should fetch at all (false in mock mode)
 */
export function useJiraData(
  activeProjectKey?: string,
  isGlobalView?: boolean,
  enabled: boolean = true,
): JiraDataState {
  const [projects, setProjects] = useState<JiraProject[]>([])
  const [projectsLoading, setProjectsLoading] = useState(false)
  const [projectsError, setProjectsError] = useState<string | null>(null)
  const [sprintSummary, setSprintSummary] = useState<SprintSummary | null>(null)
  const [sprintLoading, setSprintLoading] = useState(false)
  const [sprintError, setSprintError] = useState<string | null>(null)
  const [allSprints, setAllSprints] = useState<JiraSprint[]>([])
  const [allSprintsLoading, setAllSprintsLoading] = useState(false)
  const [boards, setBoards] = useState<JiraBoard[]>([])
  const [boardsLoading, setBoardsLoading] = useState(false)
  const [backlog, setBacklog] = useState<JiraIssue[]>([])
  const [backlogLoading, setBacklogLoading] = useState(false)
  const [recentActivity, setRecentActivity] = useState<JiraIssue[]>([])
  const [activityLoading, setActivityLoading] = useState(false)
  const [burndownData, setBurndownData] = useState<BurndownPoint[]>([])
  const [kpis, setKpis] = useState<JiraDataState['kpis']>(null)
  const fetchIdRef = useRef(0)
  const refreshProjects = useCallback(async () => {
    setProjectsLoading(true)
    setProjectsError(null)
    try {
      console.log('[useJiraData] Fetching projects from Jira...')
      const data = await fetchProjects()
      console.log(
        `[useJiraData] Got ${data.length} projects:`,
        data.map((p) => p.key),
      )
      setProjects(data)
    } catch (err: any) {
      console.error('[useJiraData] fetchProjects error:', err)
      setProjectsError(err.message || 'Error fetching projects')
    } finally {
      setProjectsLoading(false)
    }
  }, [])
  const refreshProjectData = useCallback(async (projectKey: string) => {
    const fetchId = ++fetchIdRef.current
    console.log(`[useJiraData] Fetching data for project ${projectKey}...`)
    setSprintLoading(true)
    setBacklogLoading(true)
    setActivityLoading(true)
    setAllSprintsLoading(true)
    setBoardsLoading(true)
    setSprintError(null)
    try {
      // Fetch all sprints in parallel with other data
      const [sprint, backlogData, activity, boards] = await Promise.all([
        fetchSprintSummary(projectKey).catch((e) => {
          console.error('[useJiraData] Sprint fetch error:', e)
          return null
        }),
        fetchBacklog(projectKey).catch((e) => {
          console.error('[useJiraData] Backlog fetch error:', e)
          return [] as JiraIssue[]
        }),
        fetchRecentActivity(projectKey, 15).catch((e) => {
          console.error('[useJiraData] Activity fetch error:', e)
          return [] as JiraIssue[]
        }),
        fetchBoards(projectKey).catch((e) => {
          console.error('[useJiraData] Boards fetch error:', e)
          return []
        }),
      ])
      if (fetchId !== fetchIdRef.current) return

      // Store boards for sidebar display
      setBoards(boards)
      setBoardsLoading(false)

      // Fetch all sprints from the first Scrum board (Kanban boards don't support sprints)
      const scrumBoards = boards.filter(
        (b: any) => b.type === 'scrum' || b.type === 'simple',
      )
      if (scrumBoards.length > 0) {
        try {
          const [activeSprints, futureSprints, closedSprints] =
            await Promise.all([
              fetchSprints(scrumBoards[0].id, 'active').catch(() => []),
              fetchSprints(scrumBoards[0].id, 'future').catch(() => []),
              fetchSprints(scrumBoards[0].id, 'closed').catch(() => []),
            ])
          if (fetchId === fetchIdRef.current) {
            // Order: future first, then active, then closed (most recent first)
            setAllSprints([
              ...futureSprints,
              ...activeSprints,
              ...closedSprints,
            ])
          }
        } catch (e) {
          console.error('[useJiraData] All sprints fetch error:', e)
        }
      } else {
        console.log(
          `[useJiraData] No scrum boards for ${projectKey} — skipping sprint fetch`,
        )
      }
      setAllSprintsLoading(false)
      console.log(`[useJiraData] Sprint:`, sprint ? sprint.sprint.name : 'none')
      console.log(`[useJiraData] Backlog: ${backlogData.length} issues`)
      console.log(`[useJiraData] Activity: ${activity.length} issues`)
      setSprintSummary(sprint)
      if (!sprint) setSprintError('No se encontró sprint activo')
      if (sprint) {
        setBurndownData(calculateBurndown(sprint.sprint, sprint.issues))
      } else {
        setBurndownData([])
      }
      setBacklog(backlogData)
      setRecentActivity(activity)
      const sprintIssues = sprint?.issues || []
      const allIssues = [...sprintIssues, ...backlogData]
      const openIssues = allIssues.filter(
        (i) => i.statusCategory === 'new',
      ).length
      const inProgress = allIssues.filter(
        (i) => i.statusCategory === 'indeterminate',
      ).length
      const done = sprintIssues.filter(
        (i) => i.statusCategory === 'done',
      ).length
      const blockers = allIssues.filter(
        (i) =>
          i.priority.toLowerCase().includes('high') ||
          i.priority.toLowerCase().includes('highest') ||
          i.priority.toLowerCase().includes('blocker'),
      ).length
      const totalResult = await searchIssues(
        `project = ${projectKey} AND resolution = Unresolved`,
        1,
      )
      if (fetchId !== fetchIdRef.current) return
      setKpis({
        totalIssues: totalResult.total,
        openIssues,
        inProgress,
        done,
        velocity: sprint?.completedPoints || 0,
        blockers,
      })
    } finally {
      if (fetchId === fetchIdRef.current) {
        setSprintLoading(false)
        setBacklogLoading(false)
        setActivityLoading(false)
        setAllSprintsLoading(false)
        setBoardsLoading(false)
      }
    }
  }, [])
  const refreshAll = useCallback(
    async (projectKey: string) => {
      await Promise.all([refreshProjects(), refreshProjectData(projectKey)])
    },
    [refreshProjects, refreshProjectData],
  )

  // Fetch projects when enabled (switched to live mode)
  useEffect(() => {
    if (enabled) {
      refreshProjects()
    }
  }, [enabled, refreshProjects])

  // Fetch project data when key changes (and enabled)
  useEffect(() => {
    if (!enabled) {
      // Clear all data when disabled (switched to mock)
      setSprintSummary(null)
      setAllSprints([])
      setBoards([])
      setBacklog([])
      setRecentActivity([])
      setBurndownData([])
      setKpis(null)
      return
    }
    if (activeProjectKey && !isGlobalView) {
      refreshProjectData(activeProjectKey)
    } else if (isGlobalView) {
      setSprintSummary(null)
      setAllSprints([])
      setBoards([])
      setBacklog([])
      setRecentActivity([])
      setBurndownData([])
      setKpis(null)
    }
  }, [activeProjectKey, isGlobalView, enabled, refreshProjectData])
  return {
    projects,
    projectsLoading,
    projectsError,
    sprintSummary,
    sprintLoading,
    sprintError,
    allSprints,
    allSprintsLoading,
    boards,
    boardsLoading,
    backlog,
    backlogLoading,
    recentActivity,
    activityLoading,
    burndownData,
    kpis,
    refreshProjects,
    refreshProjectData,
    refreshAll,
  }
}
